import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AppShell } from "../components/Layout";
import { api } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { UserPlus, Trash2, Edit2, Plus, Shield, FileText, Mail, Power, Database, AlertTriangle, MapPin, ShieldCheck, Check, X } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

export default function AdminPanel() {
  const [tab, setTab] = useState("users");
  return (
    <AppShell back="/dashboard" title="ADMIN">
      <div className="px-5 lg:px-10 py-5 lg:py-8 space-y-4">
        <div>
          <div className="flex items-center gap-2">
            <Shield size={18} />
            <h2 className="font-semibold text-2xl tracking-tight uppercase">Control Panel</h2>
          </div>
          <div className="label-micro mt-1">USERS · VENDORS · CATEGORIES · SITES · ACCESS · EMAIL · AUDIT</div>
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className=" w-full grid grid-cols-3 lg:grid-cols-7 bg-white border border-slate-200 p-0 h-auto">
            <TabsTrigger data-testid="admin-tab-users" value="users" className=" font-mono text-xs uppercase tracking-widest font-bold py-3 data-[state=active]:bg-black data-[state=active]:text-white">USERS</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-vendors" value="vendors" className=" font-mono text-xs uppercase tracking-widest font-bold py-3 data-[state=active]:bg-black data-[state=active]:text-white">VENDORS</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-categories" value="categories" className=" font-mono text-xs uppercase tracking-widest font-bold py-3 data-[state=active]:bg-black data-[state=active]:text-white">CATEGORIES</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-sites" value="sites" className=" font-mono text-xs uppercase tracking-widest font-bold py-3 data-[state=active]:bg-black data-[state=active]:text-white">SITES</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-access" value="access" className=" font-mono text-xs uppercase tracking-widest font-bold py-3 data-[state=active]:bg-black data-[state=active]:text-white">ACCESS</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-email" value="email" className=" font-mono text-xs uppercase tracking-widest font-bold py-3 data-[state=active]:bg-black data-[state=active]:text-white">EMAIL</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-audit" value="audit" className=" font-mono text-xs uppercase tracking-widest font-bold py-3 data-[state=active]:bg-black data-[state=active]:text-white">AUDIT</TabsTrigger>
          </TabsList>
          <TabsContent value="users" className="mt-4"><UsersTab /></TabsContent>
          <TabsContent value="vendors" className="mt-4"><VendorsTab /></TabsContent>
          <TabsContent value="categories" className="mt-4"><CategoriesTab /></TabsContent>
          <TabsContent value="sites" className="mt-4"><SitesTab /></TabsContent>
          <TabsContent value="access" className="mt-4"><AccessControlTab /></TabsContent>
          <TabsContent value="email" className="mt-4"><EmailTab /></TabsContent>
          <TabsContent value="audit" className="mt-4"><AuditTab /></TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}

function EmailTab() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [savingToggle, setSavingToggle] = useState(false);
  const [adding, setAdding] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/email/config");
      setCfg(r.data);
    } catch (e) {
      toast.error("Failed to load email config");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggle = async () => {
    setSavingToggle(true);
    try {
      const r = await api.put("/admin/email/config", { enabled: !cfg.enabled });
      setCfg(r.data);
      toast.success(`Email notifications ${r.data.enabled ? "ENABLED" : "DISABLED"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSavingToggle(false); }
  };

  const addEmail = async () => {
    const email = (newEmail || "").trim().toLowerCase();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setAdding(true);
    try {
      const r = await api.post("/admin/email/recipients", { email });
      setCfg((c) => ({ ...c, recipients: r.data.recipients }));
      setNewEmail("");
      toast.success(`Added ${email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setAdding(false); }
  };

  const removeEmail = async (email) => {
    try {
      const r = await api.delete(`/admin/email/recipients/${encodeURIComponent(email)}`);
      setCfg((c) => ({ ...c, recipients: r.data.recipients }));
      toast.success(`Removed ${email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const sendTest = async () => {
    const to = (testTo || "").trim().toLowerCase();
    if (!to || !to.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setTesting(true);
    try {
      const r = await api.post("/admin/email/test", { to });
      if (r.data?.ok) {
        toast.success(`Sent · email_id ${r.data.info?.email_id || "ok"}`);
      } else {
        toast.error(r.data?.info?.message || "Failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setTesting(false); }
  };

  if (loading || !cfg) return <div className="p-5 label-micro">LOADING…</div>;

  return (
    <div data-testid="email-tab" className="space-y-4">
      {/* Status banner */}
      <div className={`border-2 p-4 ${cfg.resend_configured ? "border-olive bg-olive/5" : "border-amber-500 bg-amber-50"}`}>
        <div className="flex items-center gap-2">
          <Mail size={14} className={cfg.resend_configured ? "text-olive" : "text-amber-600"} />
          <div className="font-mono text-[10px] uppercase tracking-widest font-bold">
            {cfg.resend_configured ? "RESEND CONFIGURED" : "RESEND_API_KEY MISSING"}
          </div>
        </div>
        <div className="font-mono text-xs mt-2 text-slate-700">
          From: <span className="font-bold">{cfg.from}</span>
        </div>
        {!cfg.resend_configured && (
          <div className="font-mono text-[10px] text-amber-700 mt-1">
            Add RESEND_API_KEY to /app/backend/.env and restart backend.
          </div>
        )}
      </div>

      {/* Toggle */}
      <div className="bg-white border border-slate-200 p-4 flex items-center justify-between">
        <div>
          <div className="label-micro">MASTER SWITCH</div>
          <div className="font-display font-bold text-lg uppercase">
            {cfg.enabled ? "Email notifications ON" : "Email notifications OFF"}
          </div>
          <div className="font-mono text-[10px] text-slate-500 mt-1">
            Triggers ONLY on new request creation. Not on approvals, payments, or edits.
          </div>
        </div>
        <button
          data-testid="email-toggle-btn"
          onClick={toggle}
          disabled={savingToggle}
          className={`px-4 py-3 font-mono text-xs uppercase tracking-widest font-bold border-2 transition-all ${
            cfg.enabled ? "bg-black text-white border-black hover:bg-olive hover:border-olive" : "bg-white text-black border-black hover:bg-black hover:text-white"
          }`}
        >
          <Power size={12} className="inline mr-2" />
          {cfg.enabled ? "DISABLE" : "ENABLE"}
        </button>
      </div>

      {/* Recipients */}
      <div className="bg-white border border-slate-200">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="label-micro">ACCOUNTS RECIPIENTS</div>
            <div className="font-mono text-xs text-slate-500 mt-0.5">
              {cfg.recipients?.length || 0} configured · ONE email sent per new request to all recipients
            </div>
          </div>
        </div>
        <div className="p-4 flex gap-2">
          <Input
            data-testid="email-recipient-input"
            type="email"
            placeholder="accounts@company.co.in"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addEmail()}
            className=" border border-slate-200 font-mono text-xs flex-1"
          />
          <button
            data-testid="email-recipient-add-btn"
            onClick={addEmail}
            disabled={adding}
            className="bg-black text-white px-4 font-mono text-xs uppercase tracking-widest font-bold hover:bg-olive disabled:opacity-50 flex items-center gap-1"
          >
            <Plus size={12} /> ADD
          </button>
        </div>
        {(cfg.recipients?.length || 0) === 0 ? (
          <div className="p-6 text-center font-mono text-xs uppercase tracking-widest text-slate-400 border-t border-slate-200">
            NO RECIPIENTS CONFIGURED · NO EMAILS WILL BE SENT
          </div>
        ) : (
          <div className="border-t border-slate-200">
            {cfg.recipients.map((email) => (
              <div key={email} data-testid={`email-recipient-${email}`} className="px-4 py-3 border-b border-slate-200 last:border-b-0 flex justify-between items-center">
                <div className="font-mono text-xs">{email}</div>
                <button
                  data-testid={`email-recipient-del-${email}`}
                  onClick={() => removeEmail(email)}
                  className="text-red-700 hover:text-red-900 font-mono text-[10px] uppercase tracking-widest font-bold flex items-center gap-1"
                >
                  <Trash2 size={11} /> REMOVE
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Test send */}
      <div className="bg-white border border-slate-200 p-4 space-y-2">
        <div className="label-micro">SEND TEST EMAIL</div>
        <div className="font-mono text-[10px] text-slate-500">
          Sends a one-off test message via Resend. Until DNS is verified, only emails on your Resend account will be accepted.
        </div>
        <div className="flex gap-2">
          <Input
            data-testid="email-test-to-input"
            type="email"
            placeholder="you@voyageofwellness.co.in"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            className=" border border-slate-200 font-mono text-xs flex-1"
          />
          <button
            data-testid="email-test-send-btn"
            onClick={sendTest}
            disabled={testing || !cfg.resend_configured}
            className="bg-black text-white px-4 font-mono text-xs uppercase tracking-widest font-bold hover:bg-olive disabled:opacity-40 flex items-center gap-1"
          >
            <Mail size={12} /> {testing ? "SENDING…" : "SEND TEST"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AuditTab() {
  const [items, setItems] = useState([]);
  const [failed, setFailed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testPhone, setTestPhone] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/admin/audit-log?limit=100"),
      api.get("/admin/failed-logins?limit=50"),
    ]).then(([a, f]) => {
      setItems(a.data);
      setFailed(f.data);
    }).finally(() => setLoading(false));
  }, []);

  const sendTest = async () => {
    setSending(true);
    try {
      const res = await api.post("/notifications/test-whatsapp", {
        phone: testPhone.trim() || undefined,
      });
      if (res.data.ok) {
        toast.success(`Sent to ${res.data.to} (SID: ${res.data.detail})`);
      } else {
        toast.error(`Failed: ${res.data.detail}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="label-micro">LOADING…</div>;
  return (
    <div className="space-y-6">
      {/* Data management — destructive actions */}
      <DataManagementBlock />

      {/* Twilio test */}
      <div className="bg-white border border-slate-200 p-4">
        <div className="label-micro mb-2">TEST WHATSAPP (Twilio Sandbox)</div>
        <div className="flex gap-2">
          <Input
            data-testid="test-wa-phone"
            placeholder="+919876543210 (leave blank to use your own)"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            className=" border border-slate-200 font-mono flex-1"
          />
          <button
            data-testid="send-test-wa"
            onClick={sendTest}
            disabled={sending}
            className="bg-black text-white px-4 font-mono text-xs uppercase tracking-widest font-bold hover:bg-olive disabled:opacity-50"
          >
            {sending ? "SENDING…" : "SEND TEST"}
          </button>
        </div>
        <div className="label-micro mt-2 text-slate-500">
          Sandbox: recipient must first send <span className="font-bold">join &lt;sandbox-code&gt;</span> to +14155238886 to opt-in.
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-2">
          <FileText size={14}/>
          <span className="label-micro">ACTIVITY LOG ({items.length})</span>
        </div>
        <div className="bg-white border border-slate-200 max-h-[500px] overflow-auto">
          {items.length === 0 && <div className="p-5 label-micro text-center">NO ACTIVITY YET</div>}
          {items.map((a) => (
            <div key={a.audit_id} data-testid={`audit-${a.audit_id}`} className="border-b border-slate-200 px-3 py-2">
              <div className="flex justify-between items-start">
                <div>
                  <span className="font-mono text-[10px] uppercase tracking-widest bg-black text-white px-1.5 py-0.5">{a.action}</span>
                  <span className="font-mono text-xs ml-2 text-slate-700">{a.user_email || "—"}</span>
                </div>
                <span className="font-mono text-[10px] text-slate-400">{new Date(a.created_at).toLocaleString("en-IN")}</span>
              </div>
              {a.target && (
                <div className="font-mono text-[11px] text-slate-500 mt-1">
                  <span className="opacity-60">target:</span> {a.target}
                </div>
              )}
              {a.details && Object.keys(a.details).length > 0 && (
                <div className="font-mono text-[10px] text-slate-500 mt-0.5 truncate">
                  {JSON.stringify(a.details)}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-2">
          <Shield size={14} className="text-red-600"/>
          <span className="label-micro text-red-600">FAILED LOGINS ({failed.length})</span>
        </div>
        <div className="bg-white border border-red-200 max-h-[250px] overflow-auto">
          {failed.length === 0 && <div className="p-5 label-micro text-center">NO FAILED ATTEMPTS</div>}
          {failed.map((f, i) => (
            <div key={i} className="border-b border-red-100 px-3 py-2 flex justify-between text-[11px] font-mono">
              <div>
                <span className="font-bold">{f.reason}</span>
                {f.email && <span className="ml-2 text-slate-600">{f.email}</span>}
              </div>
              <span className="text-slate-400 text-[10px]">{new Date(f.created_at).toLocaleString("en-IN")}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", phone: "", role: "requestor", site: "" });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [u, s] = await Promise.all([api.get("/admin/users"), api.get("/sites")]);
      setUsers(u.data);
      setSites(s.data.sites);
      setRoles(s.data.roles);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const openAdd = () => { setEditing(null); setForm({ email: "", name: "", phone: "", role: "requestor", site: "" }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ email: u.email, name: u.name || "", phone: u.phone || "", role: u.role, site: u.site || "" }); setOpen(true); };
  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/admin/users/${editing.user_id}`, { name: form.name, phone: form.phone, role: form.role, site: form.site });
        toast.success("User updated");
      } else {
        await api.post("/admin/users", form);
        toast.success("User added");
      }
      setOpen(false); fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const toggleActive = async (u) => {
    await api.patch(`/admin/users/${u.user_id}`, { active: !u.active });
    toast.success(u.active ? "User disabled" : "User enabled");
    fetchAll();
  };
  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}?`)) return;
    await api.delete(`/admin/users/${u.user_id}`);
    toast.success("User removed");
    fetchAll();
  };

  return (
    <div className="space-y-3">
      <button data-testid="add-user-btn" onClick={openAdd} className="w-full bg-black text-white py-3 font-mono text-xs uppercase tracking-widest font-bold flex justify-center items-center gap-2 hover:bg-olive transition-colors">
        <UserPlus size={14}/>ADD USER
      </button>
      {loading && <div className="label-micro">LOADING…</div>}
      <div className="space-y-0 bg-white">
        {users.map((u) => (
          <div key={u.user_id} data-testid={`user-row-${u.user_id}`} className="border-b border-slate-200/10 px-4 py-3">
            <div className="flex justify-between items-start mb-1">
              <div>
                <div className="font-display font-bold text-sm">{u.name || u.email}</div>
                <div className="font-mono text-[10px] text-slate-500">{u.email}</div>
              </div>
              <span className={`font-mono text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 ${u.active ? "bg-olive text-white" : "bg-slate-400 text-white"}`}>
                {u.active ? "ACTIVE" : "DISABLED"}
              </span>
            </div>
            <div className="flex gap-1 flex-wrap mt-1">
              <span className="font-mono text-[9px] uppercase tracking-widest font-bold bg-black text-white px-1.5 py-0.5">{u.role}</span>
              {u.site && <span className="font-mono text-[9px] uppercase tracking-widest font-bold bg-slate-200 px-1.5 py-0.5">{u.site}</span>}
              {u.phone && <span className="font-mono text-[9px] uppercase tracking-widest font-bold bg-olive/10 text-olive border border-olive px-1.5 py-0.5">{u.phone}</span>}
            </div>
            <div className="flex gap-2 mt-3">
              <button data-testid={`edit-user-${u.user_id}`} onClick={() => openEdit(u)} className="flex-1 border border-black py-1.5 font-mono text-[10px] uppercase tracking-widest font-bold hover:bg-black hover:text-white flex items-center justify-center gap-1"><Edit2 size={10}/>EDIT</button>
              <button data-testid={`toggle-user-${u.user_id}`} onClick={() => toggleActive(u)} className="flex-1 border border-black py-1.5 font-mono text-[10px] uppercase tracking-widest font-bold hover:bg-black hover:text-white">{u.active ? "DISABLE" : "ENABLE"}</button>
              <button data-testid={`delete-user-${u.user_id}`} onClick={() => remove(u)} className="flex-1 border border-red-600 text-red-600 py-1.5 font-mono text-[10px] uppercase tracking-widest font-bold hover:bg-red-600 hover:text-white flex items-center justify-center gap-1"><Trash2 size={10}/>DEL</button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md  border border-slate-200 p-0 gap-0">
          <DialogHeader className="px-5 py-4 border-b border-slate-200">
            <DialogTitle className="font-semibold uppercase">{editing ? "Edit User" : "Add User"}</DialogTitle>
          </DialogHeader>
          <div className="p-5 space-y-4">
            {!editing && (
              <Field label="EMAIL">
                <Input data-testid="user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className=" border border-slate-200" />
              </Field>
            )}
            <Field label="NAME">
              <Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className=" border border-slate-200" />
            </Field>
            <Field label="WHATSAPP / PHONE (E.164)">
              <Input
                data-testid="user-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+919876543210"
                className=" border border-slate-200 font-mono"
              />
              <div className="label-micro mt-1 text-slate-500">Include country code. Required for WhatsApp notifications.</div>
            </Field>
            <Field label="ROLE">
              <select data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full border border-slate-200 px-3 py-2 font-display font-bold uppercase">
                {roles.map((r) => <option key={r} value={r}>{r.toUpperCase()}</option>)}
              </select>
            </Field>
            <Field label="SITE">
              <select data-testid="user-site" value={form.site || ""} onChange={(e) => setForm({ ...form, site: e.target.value })} className="w-full border border-slate-200 px-3 py-2 font-display font-bold">
                <option value="">— None —</option>
                {sites.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <button data-testid="save-user-btn" onClick={save} className="w-full bg-black text-white py-3 font-mono text-xs uppercase tracking-widest font-bold hover:bg-olive">SAVE</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VendorsTab() {
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", category: "" });

  const fetchAll = () => api.get("/vendors").then((r) => setVendors(r.data));
  useEffect(() => { fetchAll(); }, []);

  const openAdd = () => { setEditing(null); setForm({ name: "", category: "" }); setOpen(true); };
  const openEdit = (v) => { setEditing(v); setForm({ name: v.name, category: v.category || "" }); setOpen(true); };
  const save = async () => {
    try {
      if (editing) await api.patch(`/vendors/${editing.vendor_id}`, form);
      else await api.post("/vendors", form);
      setOpen(false); fetchAll();
      toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (v) => {
    if (!window.confirm(`Remove ${v.name}?`)) return;
    await api.delete(`/vendors/${v.vendor_id}`);
    toast.success("Deleted"); fetchAll();
  };
  const approve = async (v) => {
    try {
      await api.patch(`/vendors/${v.vendor_id}/approve`);
      toast.success(`Approved ${v.name}`);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const reject = async (v) => {
    const reason = window.prompt(`Reject "${v.name}"? Optional reason:`);
    if (reason === null) return; // cancelled
    try {
      await api.patch(`/vendors/${v.vendor_id}/reject`, { reason: reason || null });
      toast.success(`Rejected ${v.name}`);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  // Sort: pending first, then alphabetical
  const sortedVendors = [...vendors].sort((a, b) => {
    if (!!a.pending_approval !== !!b.pending_approval) return a.pending_approval ? -1 : 1;
    return (a.name || "").localeCompare(b.name || "");
  });
  const pendingCount = vendors.filter((v) => v.pending_approval && !v.rejected).length;

  return (
    <div className="space-y-3">
      <button data-testid="add-vendor-admin-btn" onClick={openAdd} className="w-full bg-black text-white py-3 font-mono text-xs uppercase tracking-widest font-bold flex justify-center items-center gap-2 hover:bg-olive"><Plus size={14}/>ADD VENDOR</button>
      {pendingCount > 0 && (
        <div data-testid="vendors-pending-banner" className="bg-amber-50 border-2 border-amber-500 px-3 py-2 font-mono text-[11px] text-amber-800">
          {pendingCount} vendor request(s) pending approval
        </div>
      )}
      {vendors.length === 0 && <div className="p-10 text-center label-micro">NO VENDORS YET</div>}
      <div className="bg-white">
        {sortedVendors.map((v) => {
          const isRejected = v.rejected;
          const isPending = v.pending_approval && !isRejected;
          return (
            <div key={v.vendor_id} data-testid={`vendor-row-${v.vendor_id}`} className={`border-b-2 px-4 py-3 ${
              isPending ? "border-amber-500 bg-amber-50/40" : isRejected ? "border-red-300 bg-red-50/40" : "border-slate-200"
            }`}>
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold">{v.name}</span>
                    {v.is_new && !isRejected && <span className="px-1.5 py-0.5 bg-olive text-white font-mono text-[9px] uppercase tracking-widest font-bold">NEW</span>}
                    {isPending && <span className="px-1.5 py-0.5 bg-amber-500 text-white font-mono text-[9px] uppercase tracking-widest font-bold">PENDING</span>}
                    {isRejected && <span className="px-1.5 py-0.5 bg-red-600 text-white font-mono text-[9px] uppercase tracking-widest font-bold">REJECTED</span>}
                  </div>
                  {v.category && <div className="label-micro">{v.category}</div>}
                  {(v.requested_by_name || v.created_at) && (isPending || isRejected) && (
                    <div className="mt-1 font-mono text-[10px] text-slate-600 space-y-0.5">
                      {v.requested_by_name && <div>Requested by: <span className="font-bold">{v.requested_by_name}</span></div>}
                      {v.created_at && <div>Requested at: {new Date(v.created_at).toLocaleString()}</div>}
                      {v.reject_reason && <div className="text-red-700">Rejected reason: {v.reject_reason}</div>}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0 flex-wrap justify-end">
                  {isPending && (
                    <>
                      <button data-testid={`approve-vendor-${v.vendor_id}`} onClick={() => approve(v)} className="border border-olive bg-olive text-white px-3 py-1 font-mono text-[10px] uppercase tracking-widest font-bold hover:bg-black hover:border-black">APPROVE</button>
                      <button data-testid={`reject-vendor-${v.vendor_id}`} onClick={() => reject(v)} className="border border-red-600 text-red-600 px-3 py-1 font-mono text-[10px] uppercase tracking-widest font-bold hover:bg-red-600 hover:text-white">REJECT</button>
                    </>
                  )}
                  {isRejected && (
                    <button data-testid={`re-approve-vendor-${v.vendor_id}`} onClick={() => approve(v)} className="border border-olive bg-white text-olive px-3 py-1 font-mono text-[10px] uppercase tracking-widest font-bold hover:bg-olive hover:text-white">RE-APPROVE</button>
                  )}
                  <button data-testid={`edit-vendor-${v.vendor_id}`} onClick={() => openEdit(v)} className="border border-black px-3 py-1 font-mono text-[10px] uppercase tracking-widest font-bold hover:bg-black hover:text-white">EDIT</button>
                  <button data-testid={`del-vendor-${v.vendor_id}`} onClick={() => remove(v)} className="border border-red-600 text-red-600 px-3 py-1 font-mono text-[10px] uppercase tracking-widest font-bold hover:bg-red-600 hover:text-white">DEL</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md  border border-slate-200 p-0 gap-0">
          <DialogHeader className="px-5 py-4 border-b border-slate-200">
            <DialogTitle className="font-semibold uppercase">{editing ? "Edit Vendor" : "Add Vendor"}</DialogTitle>
          </DialogHeader>
          <div className="p-5 space-y-4">
            <Field label="NAME">
              <Input data-testid="vendor-admin-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className=" border border-slate-200" />
            </Field>
            <Field label="CATEGORY (OPTIONAL)">
              <Input data-testid="vendor-admin-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className=" border border-slate-200" />
            </Field>
            <button data-testid="save-vendor-btn" onClick={save} className="w-full bg-black text-white py-3 font-mono text-xs uppercase tracking-widest font-bold hover:bg-olive">SAVE</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <div className="label-micro mb-1">{label}</div>
    {children}
  </div>
);

function DataManagementBlock() {
  const [mode, setMode] = useState("test_only"); // 'range' | 'test_only'
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [preview, setPreview] = useState(null); // { would_delete, sample }
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const runPreview = async () => {
    setLoading(true);
    setPreview(null);
    try {
      const body = { mode, confirm: "preview" };
      if (mode === "range") { body.from_date = from; body.to_date = to; }
      const r = await api.post("/admin/data/preview", body);
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setLoading(false); }
  };

  const runDelete = async () => {
    if (confirmText !== "DELETE") {
      toast.error("Type DELETE to confirm");
      return;
    }
    setLoading(true);
    try {
      const body = { mode, confirm: "DELETE" };
      if (mode === "range") { body.from_date = from; body.to_date = to; }
      const r = await api.post("/admin/data/delete", body);
      toast.success(`Deleted ${r.data.deleted_requests} request(s), ${r.data.deleted_expenses} expense(s)`);
      setPreview(null);
      setConfirmOpen(false);
      setConfirmText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setLoading(false); }
  };

  const canPreview = mode === "test_only" || (from && to && from <= to);

  return (
    <div data-testid="data-mgmt-block" className="bg-white border-2 border-red-600 p-4 lg:p-5">
      <div className="flex items-center gap-2 mb-1">
        <Database size={14} className="text-red-600" />
        <span className="label-micro text-red-700">DATA MANAGEMENT · ADMIN ONLY</span>
      </div>
      <div className="font-mono text-[10px] text-slate-500 mb-4">
        Hard-deletes requests (and related expenses, notifications). Cannot be undone. Use with care.
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <button
          data-testid="dmgmt-mode-test"
          onClick={() => { setMode("test_only"); setPreview(null); }}
          className={`px-3 py-2 font-mono text-[11px] uppercase tracking-widest font-bold border-2 transition-all ${
            mode === "test_only" ? "bg-black text-white border-black" : "bg-white text-black border-slate-300 hover:border-black"
          }`}
        >
          BULK TEST ENTRIES
        </button>
        <button
          data-testid="dmgmt-mode-range"
          onClick={() => { setMode("range"); setPreview(null); }}
          className={`px-3 py-2 font-mono text-[11px] uppercase tracking-widest font-bold border-2 transition-all ${
            mode === "range" ? "bg-black text-white border-black" : "bg-white text-black border-slate-300 hover:border-black"
          }`}
        >
          DATE RANGE
        </button>
      </div>

      {mode === "range" && (
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <div className="label-micro mb-1">FROM</div>
            <input
              data-testid="dmgmt-from"
              type="date"
              value={from}
              onChange={(e) => { setFrom(e.target.value); setPreview(null); }}
              className="w-full border-b-2 border-slate-300 py-2 font-mono text-xs focus:border-black outline-none"
            />
          </div>
          <div>
            <div className="label-micro mb-1">TO</div>
            <input
              data-testid="dmgmt-to"
              type="date"
              value={to}
              onChange={(e) => { setTo(e.target.value); setPreview(null); }}
              className="w-full border-b-2 border-slate-300 py-2 font-mono text-xs focus:border-black outline-none"
            />
          </div>
        </div>
      )}
      {mode === "test_only" && (
        <div className="font-mono text-[10px] text-slate-500 mb-3">
          Matches requests where vendor name OR requester name starts with <span className="font-bold">TEST_</span> (case-insensitive).
        </div>
      )}

      <div className="flex gap-2">
        <button
          data-testid="dmgmt-preview-btn"
          onClick={runPreview}
          disabled={!canPreview || loading}
          className="bg-white text-black px-4 py-2 font-mono text-xs uppercase tracking-widest font-bold border border-slate-200 hover:bg-black hover:text-white disabled:opacity-40 transition-colors"
        >
          PREVIEW
        </button>
        <button
          data-testid="dmgmt-delete-btn"
          onClick={() => setConfirmOpen(true)}
          disabled={!preview || preview.would_delete === 0 || loading}
          className="bg-red-600 text-white px-4 py-2 font-mono text-xs uppercase tracking-widest font-bold border-2 border-red-600 hover:bg-red-700 disabled:opacity-40 transition-colors flex items-center gap-1"
        >
          <Trash2 size={11} /> DELETE
        </button>
      </div>

      {preview && (
        <div data-testid="dmgmt-preview-result" className="mt-4 border border-slate-200 bg-slate-50 p-3">
          <div className="font-semibold text-2xl tracking-tight">
            {preview.would_delete} <span className="text-slate-400 text-sm font-mono uppercase tracking-widest">would be deleted</span>
          </div>
          {preview.sample && preview.sample.length > 0 && (
            <div className="mt-3 space-y-1">
              <div className="label-micro">SAMPLE (newest 5)</div>
              {preview.sample.map((s) => (
                <div key={s.request_id} className="font-mono text-[11px] text-slate-700 flex justify-between gap-2">
                  <span>{s.request_id} · {s.site} · {s.vendor_name}</span>
                  <span className="text-slate-500">₹{Math.round(s.amount).toLocaleString("en-IN")}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className=" border-2 border-red-600 max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-semibold uppercase flex items-center gap-2">
              <AlertTriangle size={18} className="text-red-600" /> Confirm Delete
            </AlertDialogTitle>
            <AlertDialogDescription className="font-mono text-xs">
              You are about to permanently delete <span className="font-bold text-red-700">{preview?.would_delete || 0}</span> request(s) and their related expenses & notifications.
              <br/><br/>
              This cannot be undone. Type <span className="font-bold">DELETE</span> below to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            data-testid="dmgmt-confirm-input"
            placeholder="Type DELETE"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className=" border border-slate-200 font-mono"
          />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="dmgmt-cancel" className=" border-black" onClick={() => setConfirmText("")}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="dmgmt-confirm-delete"
              onClick={runDelete}
              disabled={confirmText !== "DELETE" || loading}
              className=" bg-red-600 hover:bg-red-700 text-white disabled:opacity-40"
            >
              {loading ? "DELETING…" : "DELETE PERMANENTLY"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


// ═══════════════════════════════════════════════════
// CATEGORIES TAB — Admin-only CRUD
// Forward-only rename: the OLD name stays attached to historical requests;
// the renamed row is auto-deactivated and a new active row is created with
// the new name. Toggle controls dropdown visibility, NOT historical data.
// Hard delete is intentionally not exposed; deactivation is the safe path.
// ═══════════════════════════════════════════════════
function SitesTab() {
  // v2.13 — DB-backed sites. Admins can add, rename (cascade), toggle active,
  // and delete (only when no records reference it).
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/sites");
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load sites");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addSite = async () => {
    const name = (newName || "").trim();
    if (!name) { toast.error("Enter a site name"); return; }
    setAdding(true);
    try {
      await api.post("/admin/sites", { name });
      setNewName("");
      toast.success(`Site "${name}" added`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add site");
    } finally { setAdding(false); }
  };

  const openRename = (site) => {
    setRenameTarget(site);
    setRenameValue(site.name);
    setRenameOpen(true);
  };
  const saveRename = async () => {
    if (!renameTarget) return;
    const name = (renameValue || "").trim();
    if (!name || name === renameTarget.name) { setRenameOpen(false); return; }
    setBusyId(renameTarget.site_id);
    try {
      const r = await api.patch(`/admin/sites/${renameTarget.site_id}/rename`, { name });
      const cascade = r.data?.cascade || {};
      const cascadeTotal = Object.values(cascade).reduce((a, b) => a + b, 0);
      toast.success(
        cascadeTotal > 0
          ? `Renamed to "${name}" · ${cascadeTotal} record${cascadeTotal === 1 ? "" : "s"} updated`
          : `Renamed to "${name}"`
      );
      setRenameOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    } finally { setBusyId(null); }
  };

  const toggleActive = async (site) => {
    setBusyId(site.site_id);
    try {
      await api.patch(`/admin/sites/${site.site_id}/toggle`, { active: !site.active });
      toast.success(site.active ? `"${site.name}" deactivated` : `"${site.name}" activated`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally { setBusyId(null); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.site_id);
    try {
      await api.delete(`/admin/sites/${deleteTarget.site_id}`);
      toast.success(`"${deleteTarget.name}" deleted`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusyId(null); }
  };

  const active = items.filter((s) => s.active);
  const inactive = items.filter((s) => !s.active);

  return (
    <div className="space-y-5" data-testid="sites-tab">
      <div className="bg-white border border-slate-200 p-4 space-y-3">
        <div className="label-micro">ADD NEW SITE</div>
        <div className="flex gap-2">
          <Input
            data-testid="new-site-input"
            placeholder="e.g. Cyber Hub Cafe"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className=" border border-slate-200 flex-1"
            onKeyDown={(e) => { if (e.key === "Enter") addSite(); }}
          />
          <button
            data-testid="add-site-btn"
            onClick={addSite}
            disabled={adding || !newName.trim()}
            className="bg-black text-white px-4 font-mono text-xs uppercase tracking-widest font-bold hover:bg-olive disabled:opacity-40 transition-colors flex items-center gap-1"
          >
            <Plus size={14}/> ADD
          </button>
        </div>
        <p className="font-mono text-[11px] text-slate-500 leading-relaxed">
          <span className="font-bold">Renames cascade</span> — every existing record (requests, expenses, inventory, licenses, cash flow, and user site assignments) is updated to the new name.
          A site can only be <span className="font-bold">deleted</span> when no records reference it — otherwise deactivate to hide from dropdowns without touching history.
        </p>
      </div>

      {loading ? (
        <div className="label-micro">LOADING…</div>
      ) : (
        <>
          <SiteSection
            title={`ACTIVE (${active.length})`}
            items={active}
            busyId={busyId}
            onRename={openRename}
            onToggle={toggleActive}
            onDelete={setDeleteTarget}
          />
          {inactive.length > 0 && (
            <SiteSection
              title={`INACTIVE (${inactive.length})`}
              items={inactive}
              busyId={busyId}
              onRename={openRename}
              onToggle={toggleActive}
              onDelete={setDeleteTarget}
              inactive
            />
          )}
        </>
      )}

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className=" border border-slate-200">
          <DialogHeader>
            <DialogTitle className="font-semibold tracking-tight uppercase">
              Rename · {renameTarget?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="font-mono text-xs text-slate-600 leading-relaxed">
              <span className="font-bold">Cascade rename.</span> Every request, expense, inventory record, license, cash-flow entry and user site-assignment referencing
              &quot;{renameTarget?.name}&quot; will be updated to the new name.
            </p>
            <Input
              data-testid="rename-site-input"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              className=" border border-slate-200"
              autoFocus
            />
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setRenameOpen(false)} className="border border-slate-200 px-4 py-2 font-mono text-xs uppercase tracking-widest font-bold hover:bg-slate-100">
                CANCEL
              </button>
              <button
                data-testid="rename-site-save"
                onClick={saveRename}
                disabled={!renameValue.trim() || renameValue.trim() === renameTarget?.name}
                className="bg-black text-white px-4 py-2 font-mono text-xs uppercase tracking-widest font-bold hover:bg-olive disabled:opacity-40"
              >
                SAVE
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="uppercase tracking-tight">Delete site?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <span className="font-bold">{deleteTarget?.name}</span>.
              Only possible because no records reference it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-site-confirm"
              onClick={confirmDelete}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SiteSection({ title, items, onRename, onToggle, onDelete, busyId, inactive }) {
  if (items.length === 0) return null;
  return (
    <div>
      <div className="label-micro mb-2">{title}</div>
      <div className="bg-white border border-slate-200">
        {items.map((s) => (
          <div
            key={s.site_id}
            data-testid={`site-row-${s.site_id}`}
            className={`flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 last:border-b-0 ${inactive ? "bg-slate-50" : ""}`}
          >
            <div className="min-w-0 flex-1">
              <div className="font-display font-bold text-sm uppercase truncate flex items-center gap-2">
                <MapPin size={13} className="text-olive shrink-0" />
                {s.name}
                {s.renamed_from && (
                  <span className="font-mono text-[10px] uppercase tracking-widest text-olive bg-olive/10 px-1.5 py-0.5">
                    PREVIOUSLY {s.renamed_from}
                  </span>
                )}
              </div>
              <div className="label-micro mt-0.5">
                {s.in_use ? "In use · cannot delete" : "Unused"}
                {s.seeded && " · seeded"}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                data-testid={`rename-site-${s.site_id}`}
                onClick={() => onRename(s)}
                disabled={busyId === s.site_id}
                className="border border-slate-200 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest font-bold hover:bg-black hover:text-white transition-colors flex items-center gap-1"
              >
                <Edit2 size={11}/> RENAME
              </button>
              <button
                data-testid={`toggle-site-${s.site_id}`}
                onClick={() => onToggle(s)}
                disabled={busyId === s.site_id}
                className={`border-2 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest font-bold transition-colors flex items-center gap-1 ${
                  s.active
                    ? "border-amber-600 text-amber-700 hover:bg-amber-600 hover:text-white"
                    : "border-olive text-olive hover:bg-olive hover:text-white"
                }`}
              >
                <Power size={11}/> {s.active ? "DEACTIVATE" : "ACTIVATE"}
              </button>
              {!s.in_use && (
                <button
                  data-testid={`delete-site-${s.site_id}`}
                  onClick={() => onDelete(s)}
                  disabled={busyId === s.site_id}
                  className="border-2 border-red-500 text-red-600 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest font-bold hover:bg-red-500 hover:text-white transition-colors flex items-center gap-1"
                >
                  <Trash2 size={11}/> DELETE
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function CategoriesTab() {
  // v1.11 — two independent category lists (finance vs inventory) share this UI.
  // Switching the pill above swaps the entire dataset; CRUD calls are vertical-scoped.
  const [vertical, setVertical] = useState("finance");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/categories", { params: { vertical } });
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [vertical]);

  const addCategory = async () => {
    const name = (newName || "").trim();
    if (!name) { toast.error("Enter a category name"); return; }
    setAdding(true);
    try {
      await api.post("/admin/categories", { name, vertical });
      setNewName("");
      toast.success(`Category "${name}" added to ${vertical}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    } finally { setAdding(false); }
  };

  const openRename = (cat) => {
    setRenameTarget(cat);
    setRenameValue(cat.name);
    setRenameOpen(true);
  };
  const saveRename = async () => {
    if (!renameTarget) return;
    const name = (renameValue || "").trim();
    if (!name || name === renameTarget.name) { setRenameOpen(false); return; }
    setBusyId(renameTarget.category_id);
    try {
      await api.patch(`/admin/categories/${renameTarget.category_id}/rename`, { name });
      toast.success(`Renamed to "${name}". Historical requests keep "${renameTarget.name}".`);
      setRenameOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    } finally { setBusyId(null); }
  };

  const toggleActive = async (cat) => {
    setBusyId(cat.category_id);
    try {
      await api.patch(`/admin/categories/${cat.category_id}/toggle`, { active: !cat.active });
      toast.success(cat.active ? `"${cat.name}" deactivated` : `"${cat.name}" activated`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally { setBusyId(null); }
  };

  const active = items.filter((c) => c.active);
  const inactive = items.filter((c) => !c.active);

  return (
    <div className="space-y-5" data-testid="categories-tab">
      {/* Vertical switcher pill — finance and inventory category lists are fully independent */}
      <div
        className="bg-white border border-slate-200 p-1 inline-flex gap-1 rounded-xl"
        data-testid="categories-vertical-switcher"
      >
        {[
          { v: "finance", label: "Finance categories" },
          { v: "inventory", label: "Inventory categories" },
        ].map(({ v, label }) => (
          <button
            key={v}
            data-testid={`categories-vertical-${v}`}
            onClick={() => setVertical(v)}
            className={
              "px-4 py-1.5 rounded-lg font-mono text-[11px] uppercase tracking-widest font-bold transition-colors " +
              (vertical === v
                ? "bg-olive text-white"
                : "bg-transparent text-slate-600 hover:bg-slate-50")
            }
          >
            {label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 p-4 space-y-3">
        <div className="label-micro">
          ADD NEW {vertical === "inventory" ? "INVENTORY" : "FINANCE"} CATEGORY
        </div>
        <div className="flex gap-2">
          <Input
            data-testid="new-category-input"
            placeholder={vertical === "inventory" ? "e.g. Smallwares, Bar Equipment…" : "e.g. Software Subscriptions"}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className=" border border-slate-200 flex-1"
            onKeyDown={(e) => { if (e.key === "Enter") addCategory(); }}
          />
          <button
            data-testid="add-category-btn"
            onClick={addCategory}
            disabled={adding || !newName.trim()}
            className="bg-black text-white px-4 font-mono text-xs uppercase tracking-widest font-bold hover:bg-olive disabled:opacity-40 transition-colors flex items-center gap-1"
          >
            <Plus size={14}/> ADD
          </button>
        </div>
        <p className="font-mono text-[11px] text-slate-500">
          {vertical === "inventory"
            ? "Inventory categories classify physical stock items (separate from finance). Categories in use on existing items can't be deleted — deactivate to hide from the Add Item dropdown."
            : "Categories used on existing requests can't be deleted. Deactivate to remove from the dropdown without touching history."}
        </p>
      </div>

      {loading ? (
        <div className="label-micro">LOADING…</div>
      ) : (
        <>
          <CategorySection
            title={`ACTIVE (${active.length})`}
            items={active}
            busyId={busyId}
            onRename={openRename}
            onToggle={toggleActive}
          />
          {inactive.length > 0 && (
            <CategorySection
              title={`INACTIVE (${inactive.length})`}
              items={inactive}
              busyId={busyId}
              onRename={openRename}
              onToggle={toggleActive}
              inactive
            />
          )}
          {vertical === "finance" && (
            <DuplicateDetectionConfig activeCategoryNames={active.map((c) => c.name)} />
          )}
          {vertical === "finance" && (
            <CashExpenseAutoApproveConfig activeCategoryNames={active.map((c) => c.name)} />
          )}
        </>
      )}

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className=" border border-slate-200">
          <DialogHeader>
            <DialogTitle className="font-semibold tracking-tight uppercase">
              Rename · {renameTarget?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="font-mono text-xs text-slate-600 leading-relaxed">
              <span className="font-bold">Forward-only rename.</span> Existing requests with
              "{renameTarget?.name}" will keep that name for audit. The new name applies only to future requests.
            </p>
            <Input
              data-testid="rename-category-input"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              className=" border border-slate-200"
              autoFocus
            />
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setRenameOpen(false)} className="border border-slate-200 px-4 py-2 font-mono text-xs uppercase tracking-widest font-bold hover:bg-slate-100">
                CANCEL
              </button>
              <button
                data-testid="rename-category-save"
                onClick={saveRename}
                disabled={!renameValue.trim() || renameValue.trim() === renameTarget?.name}
                className="bg-black text-white px-4 py-2 font-mono text-xs uppercase tracking-widest font-bold hover:bg-olive disabled:opacity-40"
              >
                SAVE
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CategorySection({ title, items, onRename, onToggle, busyId, inactive }) {
  if (items.length === 0) return null;
  return (
    <div>
      <div className="label-micro mb-2">{title}</div>
      <div className="bg-white border border-slate-200">
        {items.map((c) => (
          <div
            key={c.category_id}
            data-testid={`category-row-${c.category_id}`}
            className={`flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 last:border-b-0 ${inactive ? "bg-slate-50" : ""}`}
          >
            <div className="min-w-0 flex-1">
              <div className="font-display font-bold text-sm uppercase truncate flex items-center gap-2">
                {c.name}
                {c.renamed_to && (
                  <span className="font-mono text-[10px] uppercase tracking-widest text-amber-700 bg-amber-100 px-1.5 py-0.5">
                    RENAMED → {c.renamed_to}
                  </span>
                )}
                {c.renamed_from && (
                  <span className="font-mono text-[10px] uppercase tracking-widest text-olive bg-olive/10 px-1.5 py-0.5">
                    PREVIOUSLY {c.renamed_from}
                  </span>
                )}
              </div>
              <div className="label-micro mt-0.5">
                {c.usage_count > 0 ? `${c.usage_count} request${c.usage_count === 1 ? "" : "s"} use this name` : "Unused"}
                {c.seeded && " · seeded"}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                data-testid={`rename-cat-${c.category_id}`}
                onClick={() => onRename(c)}
                disabled={busyId === c.category_id}
                className="border border-slate-200 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest font-bold hover:bg-black hover:text-white transition-colors flex items-center gap-1"
              >
                <Edit2 size={11}/> RENAME
              </button>
              <button
                data-testid={`toggle-cat-${c.category_id}`}
                onClick={() => onToggle(c)}
                disabled={busyId === c.category_id}
                className={`border-2 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest font-bold transition-colors flex items-center gap-1 ${
                  c.active
                    ? "border-amber-600 text-amber-700 hover:bg-amber-600 hover:text-white"
                    : "border-olive text-olive hover:bg-olive hover:text-white"
                }`}
              >
                <Power size={11}/> {c.active ? "DEACTIVATE" : "ACTIVATE"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Phase F2 — duplicate-detection excluded-category config (finance only).
function DuplicateDetectionConfig({ activeCategoryNames }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [excluded, setExcluded] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/duplicate-detection/config");
      setCfg(r.data);
      setExcluded(r.data?.excluded_categories || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load duplicate config");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleCat = (name) => {
    setExcluded((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/duplicate-detection/config", { excluded_categories: excluded });
      setCfg(r.data);
      setExcluded(r.data?.excluded_categories || []);
      toast.success("Duplicate-detection excluded list saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  // Available list is the union of currently-active categories AND any already-excluded
  // names — so excluded categories stay visible even after they're deactivated.
  const available = Array.from(
    new Set([...(activeCategoryNames || []), ...(excluded || []), ...((cfg?.available_categories) || [])])
  ).sort((a, b) => a.localeCompare(b));

  const dirty = JSON.stringify((excluded || []).slice().sort()) !==
                JSON.stringify(((cfg?.excluded_categories) || []).slice().sort());

  return (
    <div
      data-testid="duplicate-detection-config"
      className="mt-2 bg-white border border-slate-200 rounded-2xl p-5 lg:p-6 space-y-4"
    >
      <div>
        <div className="font-semibold text-sm tracking-tight text-slate-900">
          Duplicate-detection · Excluded categories
        </div>
        <p className="text-xs text-slate-600 mt-1 leading-relaxed">
          Categories listed here are <span className="font-semibold">skipped</span> by the
          duplicate scan — repeat purchases here (daily consumables) are normal and shouldn&apos;t
          trigger a warning. Match window: <span className="font-semibold">{cfg?.window_days || 7} days</span>,
          amount tolerance: <span className="font-semibold">±{cfg?.amount_tolerance_pct || 5}%</span>.
        </p>
      </div>

      {loading ? (
        <div className="text-xs text-slate-500">Loading…</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2" data-testid="duplicate-excluded-grid">
            {available.map((name) => {
              const isExcluded = excluded.includes(name);
              return (
                <button
                  key={name}
                  type="button"
                  data-testid={`dup-excluded-pill-${name}`}
                  onClick={() => toggleCat(name)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                    isExcluded
                      ? "bg-amber-100 border-amber-300 text-amber-900"
                      : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {isExcluded ? "✓ " : ""}{name}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between flex-wrap gap-2 pt-2">
            <div className="text-xs text-slate-500">
              {excluded.length} excluded
              {cfg?.updated_at && ` · last updated by ${cfg.updated_by || "—"}`}
            </div>
            <button
              data-testid="duplicate-config-save"
              onClick={save}
              disabled={!dirty || saving}
              className="bg-olive text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-olive-700 disabled:opacity-40 transition"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}


// Phase C0 — Cash-expense auto-approve config (finance only).
// Admin whitelists categories + sets the per-request cap that qualifies a Cash
// request for the auto-approve + auto-paid path (bypasses the approver queue).
function CashExpenseAutoApproveConfig({ activeCategoryNames }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allowed, setAllowed] = useState([]);
  const [capInput, setCapInput] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/cash-expense/config");
      setCfg(r.data);
      setAllowed(r.data?.allowed_categories || []);
      setCapInput(String(r.data?.amount_cap ?? 15000));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load cash-expense config");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleCat = (name) => {
    setAllowed((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const save = async () => {
    const capValue = Number(capInput);
    if (!capValue || capValue <= 0) {
      toast.error("Amount cap must be > 0");
      return;
    }
    setSaving(true);
    try {
      const r = await api.put("/admin/cash-expense/config", {
        allowed_categories: allowed,
        amount_cap: capValue,
      });
      setCfg(r.data);
      setAllowed(r.data?.allowed_categories || []);
      setCapInput(String(r.data?.amount_cap ?? 15000));
      toast.success("Cash-expense auto-approve config saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const available = Array.from(
    new Set([...(activeCategoryNames || []), ...(allowed || []), ...((cfg?.available_categories) || [])])
  ).sort((a, b) => a.localeCompare(b));

  const dirtyAllowed = JSON.stringify((allowed || []).slice().sort()) !==
                       JSON.stringify(((cfg?.allowed_categories) || []).slice().sort());
  const dirtyCap = String(cfg?.amount_cap ?? "") !== String(Number(capInput || 0));
  const dirty = dirtyAllowed || dirtyCap;

  return (
    <div
      data-testid="cash-expense-config"
      className="mt-2 bg-white border border-slate-200 rounded-2xl p-5 lg:p-6 space-y-4"
    >
      <div>
        <div className="font-semibold text-sm tracking-tight text-slate-900">
          Auto-approved cash expenses · Category whitelist + cap
        </div>
        <p className="text-xs text-slate-600 mt-1 leading-relaxed">
          A request is <span className="font-semibold">auto-approved and auto-marked paid</span> when
          ALL of the following are true: the requester holds the{" "}
          <span className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">log_cash_expense</span>{" "}
          permission, payment mode is <span className="font-semibold">Cash</span>, amount is{" "}
          <span className="font-semibold">≤ the cap below</span>, and the category is checked here.
          These flow into category totals as paid cash and are separately labeled in the audit trail.
        </p>
      </div>

      {loading ? (
        <div className="text-xs text-slate-500">Loading…</div>
      ) : (
        <>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
              Per-request amount cap (₹)
            </div>
            <input
              data-testid="cash-expense-cap-input"
              type="number"
              min="1"
              step="1"
              value={capInput}
              onChange={(e) => setCapInput(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-olive/40"
            />
            <div className="text-[11px] text-slate-500 mt-1">
              Any Cash request over this figure follows the normal approval flow.
            </div>
          </div>

          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
              Allowed categories
            </div>
            <div className="flex flex-wrap gap-2" data-testid="cash-expense-allowed-grid">
              {available.map((name) => {
                const isAllowed = allowed.includes(name);
                return (
                  <button
                    key={name}
                    type="button"
                    data-testid={`cash-allowed-pill-${name}`}
                    onClick={() => toggleCat(name)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                      isAllowed
                        ? "bg-olive/10 border-olive/40 text-olive"
                        : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    {isAllowed ? "✓ " : ""}{name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-2 pt-2">
            <div className="text-xs text-slate-500">
              {allowed.length} allowed · cap ₹{Number(cfg?.amount_cap || 0).toLocaleString("en-IN")}
              {cfg?.updated_at && ` · last updated by ${cfg.updated_by || "—"}`}
            </div>
            <button
              data-testid="cash-expense-config-save"
              onClick={save}
              disabled={!dirty || saving}
              className="bg-olive text-white px-4 py-2 rounded-lg text-xs font-semibold hover:bg-olive-700 disabled:opacity-40 transition"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════
// ACCESS CONTROL TAB — Admin-only per-user permissions & site assignments
//
// Two-pane layout: left = user list, right = selected user's permission matrix
// + site assignment widget. Permissions resolve hybrid (role default + override);
// each toggle has 3 states: ON / OFF / INHERIT (role default).
// ═══════════════════════════════════════════════════
function AccessControlTab() {
  const [users, setUsers] = useState([]);
  const [catalog, setCatalog] = useState({ keys: [], labels: {} });
  const [sitesAll, setSitesAll] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [usersRes, catRes, sitesRes] = await Promise.all([
        api.get("/admin/users/access"),
        api.get("/admin/permissions/catalog"),
        api.get("/sites"),
      ]);
      setUsers(usersRes.data || []);
      setCatalog(catRes.data || { keys: [], labels: {} });
      setSitesAll(sitesRes.data?.sites || []);
      if (!selectedId && (usersRes.data || []).length > 0) setSelectedId(usersRes.data[0].user_id);
    } catch (e) {
      toast.error("Failed to load access control data");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const selected = users.find((u) => u.user_id === selectedId);
  const filtered = users.filter((u) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (u.email || "").toLowerCase().includes(q)
      || (u.name || "").toLowerCase().includes(q)
      || (u.role || "").toLowerCase().includes(q);
  });

  const setPermission = async (key, value) => {
    if (!selected) return;
    setSaving(true);
    try {
      const r = await api.patch(`/admin/users/${selected.user_id}/permissions`, { permissions: { [key]: value } });
      setUsers((prev) => prev.map((u) => u.user_id === selected.user_id
        ? { ...u, permissions_override: r.data.permissions_override, permissions_effective: r.data.permissions_effective }
        : u));
      toast.success(`${catalog.labels[key] || key} ${value === true ? "enabled" : value === false ? "disabled" : "inherited"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const setSites = async (patch) => {
    if (!selected) return;
    setSaving(true);
    try {
      const r = await api.patch(`/admin/users/${selected.user_id}/sites`, patch);
      setUsers((prev) => prev.map((u) => u.user_id === selected.user_id
        ? { ...u, all_sites: r.data.all_sites, assigned_sites: r.data.assigned_sites } : u));
      toast.success(r.data.all_sites ? "All-sites access granted" : `Site list updated (${r.data.assigned_sites.length} site${r.data.assigned_sites.length === 1 ? "" : "s"})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  // Phase 6 Part 1 — toggle a vertical on/off for this user.
  const toggleVertical = async (verticalKey) => {
    if (!selected) return;
    const current = selected.verticals || ["finance"];
    const next = current.includes(verticalKey)
      ? current.filter((v) => v !== verticalKey)
      : [...current, verticalKey];
    setSaving(true);
    try {
      const r = await api.patch(`/admin/users/${selected.user_id}/verticals`, { verticals: next });
      setUsers((prev) => prev.map((u) => u.user_id === selected.user_id
        ? { ...u, verticals: r.data.verticals, permissions_effective: r.data.permissions_effective }
        : u));
      toast.success(`Verticals updated: ${(r.data.verticals || []).map((v) => v.toUpperCase()).join(" + ") || "NONE"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  // Promote / demote / disable. Master Admin record is locked server-side; the
  // UI mirrors that lock to prevent accidental clicks.
  const setRoleOrActive = async (patch) => {
    if (!selected) return;
    setSaving(true);
    try {
      const r = await api.patch(`/admin/users/${selected.user_id}`, patch);
      setUsers((prev) => prev.map((u) => u.user_id === selected.user_id
        ? { ...u, role: r.data.role, active: r.data.active }
        : u));
      toast.success(patch.role ? `Role set to ${patch.role}` : (patch.active ? "User enabled" : "User disabled — sessions terminated"));
      // Optimistic reload of the full row in case effective permissions changed
      const full = await api.get("/admin/users/access");
      setUsers(full.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="label-micro p-4">LOADING ACCESS CONTROL…</div>;

  return (
    <div data-testid="access-control-tab" className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Left: user list */}
      <div className="bg-white border border-slate-200">
        <div className="border-b border-slate-200 p-3">
          <Input
            data-testid="access-user-search"
            placeholder="Search by name / email / role"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className=" border border-slate-200"
          />
        </div>
        <div className="max-h-[600px] overflow-y-auto">
          {filtered.map((u) => {
            const isSel = u.user_id === selectedId;
            return (
              <button
                key={u.user_id}
                data-testid={`access-user-${u.user_id}`}
                onClick={() => setSelectedId(u.user_id)}
                className={`w-full text-left px-4 py-3 border-b border-slate-200 last:border-b-0 transition ${isSel ? "bg-olive text-white" : "hover:bg-slate-50"}`}
              >
                <div className="font-display font-bold text-sm truncate">{u.name || u.email}</div>
                <div className={`font-mono text-[10px] uppercase tracking-widest ${isSel ? "text-white/80" : "text-slate-500"}`}>
                  {u.role} · {u.all_sites ? "ALL SITES" : `${u.assigned_sites.length} SITE${u.assigned_sites.length === 1 ? "" : "S"}`}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Right: detail */}
      <div className="lg:col-span-2 space-y-5">
        {selected ? (
          <>
            <div className="bg-white border border-slate-200 p-5" data-testid="user-identity-card">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-xl tracking-tight">{selected.name || selected.email}</div>
                    {selected.is_master_admin && (
                      <span data-testid="master-admin-badge" className="font-mono text-[10px] uppercase tracking-widest bg-amber-500 text-white px-2 py-1 inline-flex items-center gap-1 font-bold">
                        <Shield size={10}/> MASTER ADMIN
                      </span>
                    )}
                    {selected.active === false && (
                      <span className="font-mono text-[10px] uppercase tracking-widest bg-red-600 text-white px-2 py-1 font-bold">DISABLED</span>
                    )}
                  </div>
                  <div className="font-mono text-xs text-slate-500 mt-1">{selected.email}</div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <select
                    data-testid="user-role-select"
                    value={selected.role}
                    disabled={saving || selected.is_master_admin}
                    onChange={(e) => setRoleOrActive({ role: e.target.value })}
                    className="border border-slate-200 bg-white px-2 py-1.5 font-mono text-[10px] uppercase tracking-widest font-bold cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {["requestor", "approver", "accounts", "admin"].map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                  <button
                    data-testid="user-active-toggle"
                    onClick={() => setRoleOrActive({ active: !selected.active })}
                    disabled={saving || selected.is_master_admin}
                    className={`border-2 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest font-bold disabled:opacity-40 disabled:cursor-not-allowed transition ${selected.active ? "border-red-600 text-red-700 hover:bg-red-600 hover:text-white" : "border-olive text-olive hover:bg-olive hover:text-white"}`}
                  >
                    <Power size={11} className="inline mr-1"/>
                    {selected.active ? "DISABLE" : "ENABLE"}
                  </button>
                </div>
              </div>
              {selected.is_master_admin && (
                <div className="mt-3 text-[11px] font-mono text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2">
                  This account is the permanent system owner. Role, status, permissions, and site access are all locked and cannot be modified by other admins.
                </div>
              )}
            </div>

            <div className="bg-white border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <MapPin size={14}/>
                  <div className="font-mono text-xs uppercase tracking-widest font-bold">SITE ASSIGNMENT</div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="access-all-sites-toggle"
                    checked={!!selected.all_sites}
                    onChange={(e) => setSites({ all_sites: e.target.checked, assigned_sites: e.target.checked ? [] : selected.assigned_sites })}
                    disabled={saving || selected.role === "admin" || selected.is_master_admin}
                    className="w-4 h-4 accent-olive"
                  />
                  <span className="font-mono text-[11px] uppercase tracking-widest font-bold">ALL SITES</span>
                </label>
              </div>
              {!selected.all_sites && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="access-sites-grid">
                  {sitesAll.map((s) => {
                    const checked = selected.assigned_sites.includes(s);
                    return (
                      <label key={s} className={`flex items-center gap-2 px-3 py-2 border-2 cursor-pointer transition ${checked ? "border-olive bg-olive/5" : "border-slate-200 hover:border-olive/40"}`}>
                        <input
                          type="checkbox"
                          data-testid={`access-site-${s.replace(/\s+/g, "-").toLowerCase()}`}
                          checked={checked}
                          onChange={() => {
                            const next = checked
                              ? selected.assigned_sites.filter((x) => x !== s)
                              : [...selected.assigned_sites, s];
                            setSites({ all_sites: false, assigned_sites: next });
                          }}
                          disabled={saving || selected.is_master_admin}
                          className="w-4 h-4 accent-olive"
                        />
                        <span className="font-mono text-xs">{s}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              {selected.all_sites && (
                <p className="font-mono text-xs text-slate-500">
                  This user has cross-site visibility — every site, every report, every payment queue.
                </p>
              )}
            </div>

            <div className="bg-white border border-slate-200 p-5" data-testid="access-verticals-card">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck size={14}/>
                <div className="font-mono text-xs uppercase tracking-widest font-bold">VERTICALS</div>
                {selected.role === "admin" && (
                  <span className="font-mono text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 px-2 py-0.5">Admin has all verticals</span>
                )}
              </div>
              <div className="font-mono text-[11px] text-slate-500 mb-4 leading-relaxed">
                Top-level workspace access. A user without Finance cannot retrieve finance data even via direct API.
              </div>
              <div className="flex flex-wrap gap-2">
                {(catalog.verticals || []).map((v) => {
                  const userVerts = selected.verticals || ["finance"];
                  const on = userVerts.includes(v.key) || selected.role === "admin";
                  const locked = selected.role === "admin" || selected.is_master_admin;
                  return (
                    <button
                      key={v.key}
                      data-testid={`vertical-toggle-${v.key}`}
                      onClick={() => !locked && toggleVertical(v.key)}
                      disabled={saving || locked}
                      className={`border-2 px-3 py-2 font-mono text-[11px] uppercase tracking-widest font-bold transition disabled:opacity-50 disabled:cursor-not-allowed ${
                        on
                          ? "bg-olive text-white border-olive hover:bg-olive/90"
                          : "bg-white text-slate-500 border-slate-300 hover:border-slate-500"
                      }`}
                    >
                      {on ? "ON" : "OFF"} · {v.label.toUpperCase()}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="bg-white border border-slate-200 p-5">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck size={14}/>
                <div className="font-mono text-xs uppercase tracking-widest font-bold">PERMISSIONS</div>
                {selected.role === "admin" && (
                  <span className="font-mono text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 px-2 py-0.5">Admin overrides all</span>
                )}
              </div>
              <div className="font-mono text-[11px] text-slate-500 mb-4 leading-relaxed">
                Each permission has three states: <b className="text-olive">ON</b> · <b className="text-red-600">OFF</b> · <b className="text-slate-600">INHERIT</b> (use role default).
                Permissions outside the user's verticals resolve OFF regardless of override.
              </div>
              {(catalog.verticals || [{ key: null, label: "" }]).map((vGroup) => {
                const keysInGroup = (catalog.keys || []).filter((k) => {
                  const v = (catalog.vertical_for || {})[k];
                  return vGroup.key ? v === vGroup.key : !v;
                });
                if (keysInGroup.length === 0) return null;
                const userVerts = selected.verticals || ["finance"];
                const verticalActive = selected.role === "admin" || !vGroup.key || userVerts.includes(vGroup.key);
                return (
                  <div key={vGroup.key || "_other"} className="mb-5 last:mb-0">
                    {vGroup.label && (
                      <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-2">
                        <span>{vGroup.label.toUpperCase()}</span>
                        {!verticalActive && (
                          <span className="bg-slate-200 text-slate-600 px-1.5 py-0.5 text-[9px] font-bold">VERTICAL OFF</span>
                        )}
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {keysInGroup.map((key) => {
                        const override = selected.permissions_override[key];
                        const effective = selected.permissions_effective[key];
                        const overrideState = override === true ? "on" : override === false ? "off" : "inherit";
                        const dim = !verticalActive;
                        // v2.11 — card tint follows the OVERRIDE STATE, not the effective value.
                        // Previous behaviour tinted all "effective:true" cards olive, which made
                        // role-default-allowed rows visually indistinguishable from explicitly
                        // toggled-on ones. This caused permission-scanning ambiguity for admins.
                        const cardCls =
                          overrideState === "on"      ? "border-olive/60 bg-olive/10"
                          : overrideState === "off"   ? "border-red-300/70 bg-red-50/60"
                          : "border-slate-200 bg-white";
                        // Resolution hint text under the label.
                        const hint =
                          overrideState === "on"      ? "Allowed via explicit override"
                          : overrideState === "off"   ? "Denied via explicit override"
                          : (effective ? "Allowed via role default" : "Denied by role default");
                        return (
                          <div
                            key={key}
                            data-testid={`perm-row-${key}`}
                            className={`border-2 ${cardCls} ${dim ? "opacity-50" : ""} px-3 py-2.5 flex items-center justify-between gap-3`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="font-mono text-xs font-bold truncate">{catalog.labels[key] || key}</div>
                              <div className="label-micro mt-0.5" data-testid={`perm-hint-${key}`}>
                                {hint}
                              </div>
                            </div>
                            <div className="flex items-center gap-0.5 flex-shrink-0">
                              <PermPill data-testid={`perm-${key}-on`} active={overrideState === "on"} color="olive" onClick={() => setPermission(key, true)} disabled={saving || selected.role === "admin" || selected.is_master_admin}>
                                <Check size={11}/>
                              </PermPill>
                              <PermPill data-testid={`perm-${key}-off`} active={overrideState === "off"} color="red" onClick={() => setPermission(key, false)} disabled={saving || selected.role === "admin" || selected.is_master_admin}>
                                <X size={11}/>
                              </PermPill>
                              <PermPill data-testid={`perm-${key}-inherit`} active={overrideState === "inherit"} color="slate" onClick={() => setPermission(key, null)} disabled={saving || selected.role === "admin" || selected.is_master_admin}>
                                <span className="text-[9px] font-bold">INH</span>
                              </PermPill>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="bg-white border border-slate-200 p-8 font-mono text-xs text-slate-500 text-center">
            Select a user to manage access
          </div>
        )}
      </div>
    </div>
  );
}

function PermPill({ active, color, onClick, disabled, children, ...rest }) {
  const cls = active
    ? color === "olive" ? "bg-olive text-white border-olive"
      : color === "red" ? "bg-red-600 text-white border-red-600"
      : "bg-slate-600 text-white border-slate-600"
    : "bg-white text-slate-400 border-slate-200 hover:border-slate-400";
  return (
    <button {...rest} onClick={onClick} disabled={disabled}
      className={`border-2 ${cls} w-7 h-7 flex items-center justify-center transition disabled:opacity-40`}
    >{children}</button>
  );
}

